import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Upload, Download, Play, Trash2, Eye, Clock, CheckCircle, XCircle, AlertTriangle, FileText, Package, RefreshCw, Plus, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Zap } from '../components/Icons';
import { toast } from 'react-hot-toast';
import { apiGet, apiPost, apiDelete, apiPut } from '../utils/api';

const BatchManagement = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvTemplate, setCsvTemplate] = useState(null);
  const [processingBatch, setProcessingBatch] = useState(null);
  const [batchInvoices, setBatchInvoices] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [loadingBatchDetails, setLoadingBatchDetails] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedBatchForLogs, setSelectedBatchForLogs] = useState(null);
  const [batchLogs, setBatchLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Bulk operations state
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBatches, setTotalBatches] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [batchTypeFilter, setBatchTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [fbrMode, setFbrMode] = useState('validation');

  useEffect(() => {
    fetchBatches();
    fetchCsvTemplate();
  }, []);

  const fetchBatches = async (page = currentPage, size = pageSize, sort = sortBy, order = sortOrder, status = statusFilter, batchType = batchTypeFilter, inactive = showInactive) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: size.toString(),
        offset: ((page - 1) * size).toString(),
        sort_by: sort,
        sort_order: order,
        show_inactive: inactive.toString()
      });

      if (status) params.append('status', status);
      if (batchType) params.append('batch_type', batchType);

      const response = await apiGet(`/api/batch/?${params.toString()}`);
      if (response.ok) {
        const data = response.data;
        setBatches(data.batches || []);
        setTotalBatches(data.total || 0);
        setTotalPages(Math.ceil((data.total || 0) / size));
        setCurrentPage(page);
      } else {
        console.error('Error fetching batches:', response.data);
        toast.error('Failed to load batches');
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchCsvTemplate = async () => {
    try {
      console.log('Fetching CSV template...');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/batch/csv-template`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const csvContent = await response.text();
        console.log('CSV template content received:', csvContent.substring(0, 200) + '...');
        setCsvTemplate(csvContent);
      } else {
        console.error('CSV template response not OK:', response.status);
      }
    } catch (error) {
      console.error('Error fetching CSV template:', error);
    }
  };

  const fetchBatchDetails = async (batchId) => {
    setLoadingBatchDetails(true);
    try {
      const response = await apiGet(`/api/batch/${batchId}/invoices`);
      if (response.ok) {
        const data = response.data;
        setBatchInvoices(data.invoices || []);
        setBatchResults(data.batch_results || []);
        setShowBatchModal(true);
      } else {
        toast.error('Failed to load batch details');
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
      toast.error('Error fetching batch details');
    } finally {
      setLoadingBatchDetails(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      // Debug logging
      console.log('Selected CSV file:', csvFile);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      // Test FormData construction
      console.log('FormData object:', formData);
      console.log('FormData has file:', formData.has('file'));
      console.log('FormData get file:', formData.get('file'));

      // Try using direct fetch for file uploads to ensure proper handling
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await apiPost('/api/batch/csv-import', formData);

      if (response.ok) {
        toast.success(`CSV import batch created! Batch ID: ${response.data.batch_id}`);
        setShowCsvModal(false);
        setCsvFile(null);
        fetchBatches();
      } else {
        console.error('CSV upload response:', response.data);
        toast.error(`Failed to upload CSV: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('Error uploading CSV');
    }
  };

  const startBatch = async (batchId) => {
    try {
      const response = await apiPost(`/api/batch/${batchId}/start?mode=${encodeURIComponent(fbrMode)}`);
      if (response.ok) {
        toast.success('Batch processing started!');
        fetchBatches();
      } else {
        toast.error(`Failed to start batch: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error starting batch:', error);
      toast.error('Error starting batch');
    }
  };

  const processCsvBatch = async (batchId) => {
    setProcessingBatch(batchId);
    try {
      const response = await apiPost(`/api/batch/${batchId}/process-csv?mode=${encodeURIComponent(fbrMode)}`);
      if (response.ok) {
        const result = response.data;
        toast.success(`CSV processing completed! Success: ${result.successful}, Failed: ${result.failed}`);
        fetchBatches();
      } else {
        toast.error(`Failed to process CSV: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error('Error processing CSV');
    } finally {
      setProcessingBatch(null);
    }
  };

  const processFbrBatch = async (batchId) => {
    setProcessingBatch(batchId);
    try {
      const response = await apiPost(`/api/batch/${batchId}/process-fbr?mode=${encodeURIComponent(fbrMode)}`);
      if (response.ok) {
        const result = response.data;
        toast.success(`FBR batch processed! Success results may be in batch details.`);
        fetchBatches();
      } else {
        toast.error(`Failed to process FBR batch: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing FBR batch:', error);
      toast.error('Error processing FBR batch');
    } finally {
      setProcessingBatch(null);
    }
  };

  const retryFailedBatch = async (batchId) => {
    try {
      const response = await apiPost(`/api/batch/retry-failed/${batchId}`);
      if (response.ok) {
        toast.success('Retrying failed invoices...');
        fetchBatches();
      } else {
        toast.error(`Failed to retry batch: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error retrying batch:', error);
      toast.error('Error retrying batch');
    }
  };

  const downloadBatchResults = async (batchId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/batch/${batchId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `batch_${batchId}_results.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Batch results downloaded successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download batch results');
      }
    } catch (error) {
      console.error('Error downloading batch results:', error);
      toast.error('Error downloading batch results');
    }
  };

  // Bulk operations functions
  const handleSelectBatch = (batchId) => {
    setSelectedBatches(prev => {
      if (prev.includes(batchId)) {
        return prev.filter(id => id !== batchId);
      } else {
        return [...prev, batchId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBatches([]);
      setSelectAll(false);
    } else {
      const allBatchIds = batches.map(batch => batch.batch_id);
      setSelectedBatches(allBatchIds);
      setSelectAll(true);
    }
  };

  const bulkDeactivateBatches = async () => {
    if (selectedBatches.length === 0) {
      toast.error('Please select batches to deactivate');
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await apiPost('/api/batch/bulk-deactivate', selectedBatches);

      if (response.ok) {
        toast.success(`Successfully deactivated ${response.data.deactivated_count} batches`);
        setSelectedBatches([]);
        setSelectAll(false);
        fetchBatches();
      } else {
        toast.error(response.data?.detail || 'Failed to deactivate batches');
      }
    } catch (error) {
      console.error('Error bulk deactivating batches:', error);
      toast.error('Error bulk deactivating batches');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkReactivateBatches = async () => {
    if (selectedBatches.length === 0) {
      toast.error('Please select batches to reactivate');
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await apiPost('/api/batch/bulk-reactivate', selectedBatches);

      if (response.ok) {
        toast.success(`Successfully reactivated ${response.data.reactivated_count} batches`);
        setSelectedBatches([]);
        setSelectAll(false);
        fetchBatches();
      } else {
        toast.error(response.data?.detail || 'Failed to reactivate batches');
      }
    } catch (error) {
      console.error('Error bulk reactivating batches:', error);
      toast.error('Error bulk reactivating batches');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const viewBatchLogs = async (batchId) => {
    setSelectedBatchForLogs(batchId);
    setShowLogsModal(true);
    setBatchLogs([]);
    setLogsLoading(true);

    try {
      // Start streaming logs
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/batch/${batchId}/logs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim()) {
            try {
              const logEntry = JSON.parse(line);
              setBatchLogs(prev => [...prev, logEntry]);
            } catch (e) {
              // Handle non-JSON lines
              if (line.includes('data:')) {
                const data = line.replace('data: ', '');
                if (data.trim()) {
                  try {
                    const logEntry = JSON.parse(data);
                    setBatchLogs(prev => [...prev, logEntry]);
                  } catch (e) {
                    // Add as plain text log
                    setBatchLogs(prev => [...prev, {
                      timestamp: new Date().toISOString(),
                      level: 'info',
                      message: data,
                      type: 'text'
                    }]);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error fetching batch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to deactivate this batch? It will be hidden from the list but can be reactivated later.')) {
      return;
    }

    try {
      const response = await apiDelete(`/api/batch/${batchId}`);
      if (response.ok) {
        toast.success('Batch deactivated successfully!');
        fetchBatches();
      } else {
        toast.error(`Failed to deactivate batch: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deactivating batch:', error);
      toast.error('Error deactivating batch');
    }
  };

  const reactivateBatch = async (batchId) => {
    try {
      const response = await apiPut(`/api/batch/${batchId}/reactivate`);
      if (response.ok) {
        toast.success('Batch reactivated successfully!');
        fetchBatches();
      } else {
        toast.error(`Failed to reactivate batch: ${response.data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error reactivating batch:', error);
      toast.error('Error reactivating batch');
    }
  };

  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortBy(column);
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchBatches(1, pageSize, column, newOrder, statusFilter, batchTypeFilter, showInactive);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBatches(page, pageSize, sortBy, sortOrder, statusFilter, batchTypeFilter, showInactive);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchBatches(1, size, sortBy, sortOrder, statusFilter, batchTypeFilter, showInactive);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'batchType') {
      setBatchTypeFilter(value);
    } else if (filterType === 'showInactive') {
      setShowInactive(value);
    }
    setCurrentPage(1);
    fetchBatches(1, pageSize, sortBy, sortOrder,
      filterType === 'status' ? value : statusFilter,
      filterType === 'batchType' ? value : batchTypeFilter,
      filterType === 'showInactive' ? value : showInactive
    );
  };

  const clearFilters = () => {
    setStatusFilter('');
    setBatchTypeFilter('');
    setShowInactive(false);
    setCurrentPage(1);
    fetchBatches(1, pageSize, sortBy, sortOrder, '', '', false);
  };

  const getBatchStatusIcon = (status) => {
    switch (status) {
      case 'created': return <Clock className="h-5 w-5 text-blue-500" />;
      case 'processing': return <RefreshCw className="h-5 w-5 text-yellow-500 animate-spin" />;
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getBatchStatusColor = (status) => {
    switch (status) {
      case 'created': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getBatchTypeIcon = (type) => {
    switch (type) {
      case 'csv_import': return <Package className="h-5 w-5" />;
      default: return <Package className="h-5 w-5" />;
    }
  };

  const getBatchTypeLabel = (type) => {
    switch (type) {
      case 'csv_import': return 'CSV Import';
      case 'fbr_submission': return 'FBR Submission';
      default: return type;
    }
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'success':
        return 'text-green-400';
      case 'info':
        return 'text-blue-400';
      default:
        return 'text-green-400';
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' ?
      <ChevronUp className="h-4 w-4 text-blue-600" /> :
      <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/batch/xlsx-template`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        toast.error('Could not download the template. Please try again.');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'batch_invoice_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Could not download the template. Please try again.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    // Check the extension rather than the MIME type. Browsers report a handful of different
    // MIME types for the same .xlsx depending on the OS, and some report none at all.
    const name = (file && file.name ? file.name : '').toLowerCase();
    const allowed = ['.xlsx', '.xlsm', '.csv'];
    if (file && allowed.some((ext) => name.endsWith(ext))) {
      setCsvFile(file);
    } else {
      toast.error('Please select an Excel (.xlsx) or CSV file');
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading batch information...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Batch Management</h1>
                <p className="text-gray-600">Manage CSV imports and batch processing for FBR compliance</p>

                {/* CSV Format Difference Note */}

              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/direct-fbr-submission')}
                  className="bg-amber-100 text-amber-800 px-4 py-2 rounded-md hover:bg-amber-200 flex items-center font-medium"
                  title="Submit a CSV straight to FBR without creating clients/products/invoices"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Direct FBR Submission
                </button>
                <button
                  onClick={() => setShowCsvModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import Invoices
                </button>
                {csvTemplate && (
                  <button
                    onClick={() => {
                      // Download the Excel template. A .csv carries no cell formatting, so
                      // Excel rewrites the date column to whatever order the local machine
                      // uses. The .xlsx keeps invoiceDate formatted as text instead.
                      downloadTemplate();
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Batch Overview */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Batch Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <Clock className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-blue-600">Created</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {batches.filter(b => b.status === 'created').length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <RefreshCw className="h-8 w-8 text-yellow-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-yellow-600">Processing</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {batches.filter(b => b.status === 'processing').length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-green-600">Completed</p>
                      <p className="text-2xl font-bold text-green-900">
                        {batches.filter(b => b.status === 'completed').length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <XCircle className="h-8 w-8 text-red-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-red-600">Failed</p>
                      <p className="text-2xl font-bold text-red-900">
                        {batches.filter(b => b.status === 'failed').length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CSV Template Notes */}
              {csvTemplate && csvTemplate.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Template Notes:</h4>
                  <div className="text-sm text-blue-700 whitespace-pre-line">{csvTemplate.notes}</div>
                </div>
              )}

              {/* Filters and Controls */}
              <div className="mb-6 space-y-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value="">All Statuses</option>
                      <option value="created">Created</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Type:</label>
                    <select
                      value={batchTypeFilter}
                      onChange={(e) => handleFilterChange('batchType', e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value="">All Types</option>
                      <option value="csv_import">CSV Import</option>
                      <option value="fbr_submission">FBR Submission</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Page Size:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">FBR Mode:</label>
                    <select
                      value={fbrMode}
                      onChange={(e) => setFbrMode(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                    >
                      <option value="validation">Validation</option>
                      <option value="posting">Posting</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => handleFilterChange('showInactive', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Show Inactive</span>
                    </label>
                  </div>

                  <button
                    onClick={clearFilters}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Clear Filters
                  </button>
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalBatches)} of {totalBatches} batches
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 text-sm rounded-md ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedBatches.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-blue-800">
                        {selectedBatches.length} batch{selectedBatches.length !== 1 ? 'es' : ''} selected
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={bulkDeactivateBatches}
                          disabled={bulkActionLoading}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {bulkActionLoading ? 'Deactivating...' : 'Deactivate Selected'}
                        </button>
                        <button
                          onClick={bulkReactivateBatches}
                          disabled={bulkActionLoading}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {bulkActionLoading ? 'Reactivating...' : 'Reactivate Selected'}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedBatches([]);
                        setSelectAll(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('batch_id')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Batch ID</span>
                          {getSortIcon('batch_id')}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('batch_type')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Type</span>
                          {getSortIcon('batch_type')}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('progress')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Progress</span>
                          {getSortIcon('progress')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Success/Failed
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created</span>
                          {getSortIcon('created_at')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {batches.map((batch) => (
                      <tr key={batch.batch_id} className={!batch.is_active ? 'bg-gray-100 opacity-75' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedBatches.includes(batch.batch_id)}
                            onChange={() => handleSelectBatch(batch.batch_id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {batch.batch_id.substring(0, 8)}...
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getBatchTypeIcon(batch.batch_type)}
                            <span className="ml-2 text-sm text-gray-900">
                              {getBatchTypeLabel(batch.batch_type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBatchStatusColor(batch.status)}`}>
                              {getBatchStatusIcon(batch.status)}
                              <span className="ml-1">{batch.status}</span>
                            </span>
                            {!batch.is_active && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Inactive
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${batch.progress || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-900">{batch.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="text-green-600">{batch.successful_items || 0}</span> /
                          <span className="text-red-600">{batch.failed_items || 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(batch.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {/* Removed obsolete View Details action */}

                          <button
                            onClick={() => viewBatchLogs(batch.batch_id)}
                            className="text-purple-600 hover:text-purple-900"
                            title="View Logs"
                          >
                            <FileText className="h-4 w-4" />
                          </button>

                          <button
                            onClick={() => navigate(`/batch/${batch.batch_id}/invoices`)}
                            className="text-green-600 hover:text-green-900"
                            title="View Invoices"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {batch.status === 'created' && (
                            <button
                              onClick={() => startBatch(batch.batch_id)}
                              className="text-green-600 hover:text-green-900"
                              title="Start Processing"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}

                          {batch.status === 'processing' && batch.batch_type === 'csv_import' && (
                            <button
                              onClick={() => processCsvBatch(batch.batch_id)}
                              disabled={processingBatch === batch.batch_id}
                              className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                              title="Process CSV"
                            >
                              {processingBatch === batch.batch_id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {batch.status === 'processing' && batch.batch_type === 'fbr_submission' && (
                            <button
                              onClick={() => processFbrBatch(batch.batch_id)}
                              disabled={processingBatch === batch.batch_id}
                              className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                              title="Submit FBR Batch"
                            >
                              {processingBatch === batch.batch_id ? (
                                <Play className="h-4 w-4 animate-spin" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {batch.status === 'completed' && batch.failed_items > 0 && (
                            <button
                              onClick={() => startBatch(batch.batch_id)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Restart - Process Failed Invoices"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          )}

                          {batch.status === 'completed' && (
                            <button
                              onClick={() => downloadBatchResults(batch.batch_id)}
                              className="text-green-600 hover:text-green-900"
                              title="Download Results"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          )}

                          {batch.status === 'failed' && (
                            <button
                              onClick={() => retryFailedBatch(batch.batch_id)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Retry Failed"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          )}

                          {!batch.is_active ? (
                            <button
                              onClick={() => reactivateBatch(batch.batch_id)}
                              className="text-green-600 hover:text-green-900"
                              title="Reactivate Batch"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          ) : batch.status !== 'processing' && (
                            <button
                              onClick={() => deleteBatch(batch.batch_id)}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate Batch"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* CSV Upload Modal */}
        {showCsvModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upload CSV File for FBR Submission</h3>

                {/* CSV Format Guide */}
                <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">📋 FBR CSV Format Requirements</h4>
                  <div className="text-xs text-yellow-700 space-y-1">
                    <p><strong>Required Columns:</strong> invoice_ref, client_name, buyer_ntn_cnic, buyer_registration_type, buyer_address, buyer_province, product_name, quantity, unit_price, tax_percentage, due_date</p>
                    <p><strong>Note:</strong> Use same invoice_ref for multiple products in one invoice. Each row = one product line item.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice File (Excel or CSV)
                    </label>
                    <input
                      type="file"
                      accept=".xlsx,.xlsm,.csv"
                      onChange={handleFileChange}
                      className="w-full border rounded px-3 py-2"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Excel (.xlsx) is recommended. Dates in a .csv can be rewritten by Excel
                      into your computer's date order, which changes what gets filed.
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowCsvModal(false);
                        setCsvFile(null);
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCsvUpload}
                      disabled={!csvFile}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Upload CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Batch Details Modal */}
        {showBatchModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-5/6 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Batch Details & Invoice Status</h3>
                  <button
                    onClick={() => {
                      setShowBatchModal(false);
                      setBatchInvoices([]);
                      setBatchResults([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {loadingBatchDetails ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading batch details...</p>
                  </div>
                ) : (
                  <>
                    {/* Batch Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Total Invoices</p>
                        <p className="text-2xl font-bold text-gray-900">{batchInvoices.length}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Successful</p>
                        <p className="text-2xl font-bold text-green-600">
                          {batchResults.filter(r => r.success).length}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Failed</p>
                        <p className="text-2xl font-bold text-red-600">
                          {batchResults.filter(r => !r.success).length}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">Success Rate</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {batchInvoices.length > 0
                            ? Math.round((batchResults.filter(r => r.success).length / batchInvoices.length) * 100)
                            : 0}%
                        </p>
                      </div>
                    </div>

                    {/* Invoice List with Status */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">Invoice Status Details</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Invoice #
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                FBR Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                FBR ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Error
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {batchInvoices.map((invoice, index) => {
                              const result = batchResults.find(r => r.invoice_id === invoice.id);
                              const isSuccess = result?.success || false;
                              const fbrId = result?.fbr_invoice_id || invoice.fbr_invoice_id;
                              const error = result?.error || invoice.fbr_error;

                              return (
                                <tr key={invoice.id} className={isSuccess ? 'bg-green-50' : 'bg-red-50'}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {invoice.invoice_number}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {invoice.client_name || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ₹{invoice.total_amount?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      isSuccess
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {isSuccess ? (
                                        <>
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                          Success
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="h-3 w-3 mr-1" />
                                          Failed
                                        </>
                                      )}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {fbrId || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                    {error ? (
                                      <div className="truncate" title={error}>
                                        {error.length > 50 ? `${error.substring(0, 50)}...` : error}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No error</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button
                                      onClick={() => {
                                        // Navigate to invoice edit page
                                        window.location.href = `/invoices/${invoice.id}/edit`;
                                      }}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Edit Invoice"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    {!isSuccess && (
                                      <button
                                        onClick={() => {
                                          // Retry single invoice
                                          toast.info('Retry functionality coming soon...');
                                        }}
                                        className="text-orange-600 hover:text-orange-900"
                                        title="Retry Invoice"
                                      >
                                        <RefreshCw className="h-4 w-4" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Batch Actions */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        {batchResults.filter(r => !r.success).length > 0 && (
                          <span className="text-red-600">
                            {batchResults.filter(r => !r.success).length} invoices failed
                          </span>
                        )}
                      </div>
                      <div className="space-x-3">
                        {batchResults.filter(r => !r.success).length > 0 && (
                          <button
                            onClick={() => {
                              toast.info('Retry failed invoices functionality coming soon...');
                            }}
                            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry Failed
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setShowBatchModal(false);
                            setBatchInvoices([]);
                            setBatchResults([]);
                          }}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Batch Logs Modal */}
        {showLogsModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-5/6 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Batch Processing Logs</h3>
                  <button
                    onClick={() => {
                      setShowLogsModal(false);
                      setBatchLogs([]);
                      setSelectedBatchForLogs(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Batch ID: {selectedBatchForLogs}
                  </p>
                </div>

                <div className="bg-black text-green-400 p-4 rounded-lg h-96 overflow-y-auto font-mono text-sm">
                  {logsLoading && batchLogs.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-400 mx-auto"></div>
                      <p className="mt-2 text-green-400">Loading logs...</p>
                    </div>
                  ) : batchLogs.length === 0 ? (
                    <p className="text-gray-500">No logs available for this batch.</p>
                  ) : (
                    <div className="space-y-1">
                      {batchLogs.map((log, index) => (
                        <div key={index} className={`${getLogColor(log.level)}`}>
                          <span className="text-gray-500">
                            [{new Date(log.timestamp).toLocaleTimeString()}]
                          </span>
                          <span className="ml-2 font-bold">
                            [{log.level?.toUpperCase() || 'INFO'}]
                          </span>
                          <span className="ml-2">{log.message}</span>
                          {log.details && (
                            <div className="ml-4 text-xs text-gray-400">
                              {typeof log.details === 'object' ?
                                JSON.stringify(log.details, null, 2) :
                                log.details
                              }
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => {
                      setShowLogsModal(false);
                      setBatchLogs([]);
                      setSelectedBatchForLogs(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Navigation>
  );
};

export default BatchManagement;
